import { useApi } from '@backstage/core-plugin-api';
import { DocRatingSummary } from '@internal/backstage-plugin-doc-feedback-common';
import { Button, Typography, makeStyles } from '@material-ui/core';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import { useEffect, useState } from 'react';
import { docFeedbackApiRef } from '../../api';
import { openFeedbackDialog, useDocPage } from './useDocPage';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(6),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  question: {
    fontWeight: theme.typography.fontWeightMedium as number,
    marginRight: theme.spacing(1),
  },
  selected: {
    backgroundColor: theme.palette.action.selected,
  },
  meta: {
    color: theme.palette.text.secondary,
  },
}));

/**
 * "Was this page helpful?" bar rendered at the bottom of each docs page (inside
 * the TechDocs shadow DOM).
 */
export const HelpfulAddon = () => {
  const classes = useStyles();
  const api = useApi(docFeedbackApiRef);
  const { entityRef, pagePath } = useDocPage();
  const [summary, setSummary] = useState<DocRatingSummary>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSummary(undefined);
    setError(false);
    api
      .getRatingSummary({ entityRef, pagePath })
      .then(result => !cancelled && setSummary(result))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [api, entityRef, pagePath]);

  const vote = async (helpful: boolean) => {
    setSubmitting(true);
    setError(false);
    try {
      setSummary(await api.rate({ entityRef, pagePath, helpful }));
      if (!helpful) {
        openFeedbackDialog({ category: 'inaccuracy' });
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const userVote = summary?.userVote;

  return (
    <div className={classes.root} data-testid="doc-helpful-bar">
      <Typography variant="body1" className={classes.question}>
        Was this page helpful?
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ThumbUpOutlinedIcon />}
        aria-pressed={userVote === true}
        className={userVote === true ? classes.selected : undefined}
        disabled={submitting}
        onClick={() => vote(true)}
      >
        Yes
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ThumbDownOutlinedIcon />}
        aria-pressed={userVote === false}
        className={userVote === false ? classes.selected : undefined}
        disabled={submitting}
        onClick={() => vote(false)}
      >
        No
      </Button>
      <Typography variant="body2" className={classes.meta}>
        {error && 'Could not save your vote, please try again.'}
        {!error && userVote === true && 'Thanks for letting us know!'}
        {!error && userVote === false && (
          <>
            Sorry about that.{' '}
            <Button
              size="small"
              color="primary"
              onClick={() => openFeedbackDialog({ category: 'inaccuracy' })}
            >
              Tell us what to improve
            </Button>
          </>
        )}
      </Typography>
      {summary && summary.total > 0 && summary.helpfulPercent !== undefined && (
        <Typography variant="caption" className={classes.meta}>
          {summary.helpfulPercent}% of {summary.total} readers found this page
          helpful
        </Typography>
      )}
    </div>
  );
};
